import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { accountRoleSchema } from '../../api/apiSchemas';
import { useCompleteOnboarding, useSession } from '../../auth/authQueries';
import { Brand, CenteredCardLayout } from '../../components/layout/CenteredCardLayout';
import { Alert, Button, CheckboxField, SelectField, TextField } from '../../components/ui';
import { ACCOUNT_ROLE_OPTIONS } from '../../lib/accountRoles';
import { browserNavigation } from '../../lib/browserNavigation';
import { CURRENT_TERMS_VERSION } from '../../lib/legal';
import { ROUTES } from '../../lib/routes';
import { toSafeTarget } from '../../lib/safeNavigation';
import { track } from '../../lib/logger';

const onboardingSchema = z.object({
  displayName: z
    .string()
    .trim()
    .min(2, 'Tên hiển thị cần ít nhất 2 ký tự')
    .max(80, 'Tên hiển thị tối đa 80 ký tự'),
  accountRole: accountRoleSchema,
  acceptTerms: z.literal(true, {
    errorMap: () => ({ message: 'Bạn cần đồng ý với Điều khoản sử dụng.' }),
  }),
  acceptPrivacy: z.literal(true, {
    errorMap: () => ({ message: 'Bạn cần đồng ý với Chính sách bảo mật.' }),
  }),
});

type OnboardingValues = z.infer<typeof onboardingSchema>;

export default function OnboardingPage() {
  const navigate = useNavigate();
  const session = useSession();
  const mutation = useCompleteOnboarding();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const prefillName =
    session.data?.authenticated && session.data.user.displayName
      ? session.data.user.displayName
      : '';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OnboardingValues>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: { displayName: prefillName, accountRole: 'student' },
  });

  function onSubmit(values: OnboardingValues): void {
    setSubmitError(null);
    track('auth_onboarding_submitted', { route: ROUTES.onboarding });
    mutation.mutate(
      {
        displayName: values.displayName,
        accountRole: values.accountRole,
        termsVersion: CURRENT_TERMS_VERSION,
        privacyAccepted: true,
      },
      {
        onSuccess: ({ nextUrl }) => {
          const target = toSafeTarget(nextUrl, ROUTES.account);
          if (target.startsWith('/')) navigate(target, { replace: true });
          else browserNavigation.replace(target);
        },
        onError: () => {
          setSubmitError('Không thể hoàn tất. Vui lòng kiểm tra thông tin và thử lại.');
        },
      },
    );
  }

  return (
    <CenteredCardLayout>
      <div className="stack">
        <Brand />
        <header>
          <h1>Hoàn tất hồ sơ</h1>
          <p className="muted">Chỉ vài thông tin để bắt đầu sử dụng nền tảng.</p>
        </header>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {submitError ? (
            <Alert tone="error" live>
              {submitError}
            </Alert>
          ) : null}

          <TextField
            label="Tên hiển thị"
            autoComplete="name"
            error={errors.displayName?.message}
            {...register('displayName')}
          />

          <SelectField
            label="Bạn là?"
            options={ACCOUNT_ROLE_OPTIONS}
            error={errors.accountRole?.message}
            {...register('accountRole')}
          />

          <CheckboxField
            label="Tôi đồng ý với Điều khoản sử dụng."
            error={errors.acceptTerms?.message}
            {...register('acceptTerms')}
          />
          <CheckboxField
            label="Tôi đồng ý với Chính sách bảo mật."
            error={errors.acceptPrivacy?.message}
            {...register('acceptPrivacy')}
          />

          <Button
            type="submit"
            fullWidth
            loading={mutation.isPending}
            disabled={mutation.isPending}
          >
            Tiếp tục
          </Button>
        </form>
      </div>
    </CenteredCardLayout>
  );
}
